// src/views/AuthGuard.tsx · 路由级锁（EP-010-S4 · UX 文档 §2.2 / §2.3 / §7 F1 / F6）
//
// ★ 老板 2026-09-25 拍板：强制登录**不可跳过**。UI 朴素 ≠ 可跳过。
//   本守卫就是那条红线在代码里的落点：
//     - authState === 'authenticated' → 放行 children（主视图）
//     - 其它（'unknown' / 'login_required'）→ 渲染 AuthPanel，**没有第二条路**
//
// ★ §v3-J 真相源原则（backend-proposal.md §v3-J.1 / §v3-J.2）：
//   登录态判定**只**走云端 GET /auth/me 的返回值（probeAuthState），
//   **不读浏览器本地存储**（本文件对该存储 API 零引用 —— 验收可直接 grep 验证为 0 处）。
//   断网 / 5xx → 判定不明 → **保持原状态**（不误判为已登录，也不误判为必须登录），
//   渲染侧 fail-closed 继续显示 AuthPanel（宁可多一次登录，绝不放行未验证的会话）。
//
// ★ 为什么不是真路由（react-router）：项目当前**零路由依赖**（package.json 无 react-router），
//   引入一个路由库只为这一道锁不划算（老板要"最简单"）。因此做法是
//   **状态守卫 + history.replaceState 同步 URL**：刷新 / 深链 / 直接敲地址都落在 /auth，
//   未登录时 URL 就是 /auth（UX §2.2 路由级锁的可观测证据）。
//   将来真要上多路由（/space、/reveal 深链），再换 react-router，本守卫的判定逻辑可原样复用。

import { useEffect, type ReactNode } from 'react';
import { useGameStore, probeAuthState, markAuthenticated } from '../store';
import { AuthPanel } from './AuthPanel';

export interface AuthGuardProps {
  children: ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps) {
  const authState = useGameStore((s) => s.authState);

  // 挂载时向云端问一次"我是谁"（§v3-C.5 bootstrap：先 refresh cookie 换 token，再 /auth/me）
  useEffect(() => {
    void probeAuthState();
  }, []);

  // URL 与登录态同步：未登录 → /auth；已登录 → 回根路径（刷新 / 深链同样落在 /auth）
  useEffect(() => {
    const target = authState === 'authenticated' ? '/' : '/auth';
    if (window.location.pathname !== target) {
      window.history.replaceState(null, '', target);
    }
  }, [authState]);

  if (authState === 'authenticated') {
    return <>{children}</>;
  }

  // ★ 唯一的 onAuthed 出口：登录成功（云端 200）才可能走到这里；没有 skip / close 分支
  return <AuthPanel onAuthed={markAuthenticated} />;
}

// ★ 说明（写给 review）：markAuthenticated 只把 authState 置为 'authenticated'，
//   **不**写浏览器本地存储、**不**写 cookie（cookie 由服务端 Set-Cookie 下发）。
//   下次刷新时真正的判定仍由 probeAuthState 的云端 GET /auth/me 返回值复核 ——
//   这是 §v3-J 真相源原则在认证层的直接体现：前端**不**自己宣布自己已登录。
